"use client";

import Footer from "@/app/components/footer";
import Header from "@/app/components/header";
import {
  Box,
  Button,
  Container,
  Skeleton,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import PublicShowService, { PublicShow } from "@/app/_services/PublicShowService";
import { NAVIGATION_URLS } from "@/app/_utils/constants";
import { trackWaitlistClick } from "@/app/_utils/waitlistAnalytics";
import {
  KIDSUPER_FUNNY_BUSINESS_TICKET_URL,
  isKidSuperFunnyBusinessShow,
  publicShowPurchaseUrl,
} from "@/app/_utils/publicProductUrl";
import { TicketsLayoutHelmet } from "@/app/(public)/_seo/PublicLayoutMetadata";
import { JsonLdScript } from "@/app/_seo/JsonLdScript";
import {
  breadcrumbSchema,
  eventSchema,
  jsonLdGraph,
  organizationSchema,
} from "@/app/_seo/schema";
import { absolutePublicUrl } from "@/app/_utils/constants";
import { parseShowInfo } from "@/app/_utils/parseShowInfo";
import {
  HELD_LABEL,
  combinedAvailability,
  showAvailability,
  type Availability,
} from "@/app/_utils/availability";
import {
  formatShowDateLine,
  formatShowStartTimeLine,
  parseShowDateTime,
} from "@/app/_utils/showDateTime";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import CheckIcon from "@mui/icons-material/Check";
import {
  DAD_HAT_IMG,
  KATZ_HAT_IMG,
  KATZ_MERCH_LIVE,
  KATZ_SHIRTS_IMG,
  MERCH_HUB_BLURB,
  MERCH_HUB_EYEBROW,
  MERCH_HUB_HREF,
  MERCH_HUB_TITLE,
} from "@/app/(public)/merch/dadHat";

const MERCH_THUMBS = [
  { src: DAD_HAT_IMG, alt: "ComedyUO Hat" },
  ...(KATZ_MERCH_LIVE
    ? [
        { src: KATZ_HAT_IMG, alt: "Katz's Pastrami Comedy Hat" },
        { src: KATZ_SHIRTS_IMG, alt: "Katz's Pastrami Comedy Shirts" },
      ]
    : []),
];

const pageFont =
  '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

const PAGE_BG = "#0a0a0a";
const CARD_BG = "#141414";
const CARD_BORDER = "rgba(255,255,255,0.12)";
const MUTED = "rgba(255,255,255,0.55)";
const MUTED_SOFT = "rgba(255,255,255,0.4)";

const SKELETON_CARD_KEYS = ["s0", "s1", "s2"] as const;
const skeletonLineSx = { bgcolor: "rgba(255,255,255,0.1)" };

/**
 * Offline/API-failure fallback only. Must match currently published upcoming
 * shows (including sold-out). Never put past shows here.
 */
const PREVIEW_SHOWS: PublicShow[] = [
  {
    id: "cf108557-386a-4909-9b43-d693fed27f55",
    slug: "comedy-court-bringing-killers-to-the-courtroom",
    title: "Comedy Court: Bringing Killers to the Courtroom",
    variant_name: null,
    description:
      "Tickets go live September 24th at 7pm!\n\nComedians are killing every night and we're putting them on trial. October 13th come be a witness or juror while these murderers take the stand. \n\nTickets include free drinks and snacks\n\nShow Timing\nDoors Open: 7:00PM\nShow Starts: 8:00PM\n\nFordham Moot Court Room \n150 West 62nd Street\n\n-----\n\nThis is a 21+ event - security will be checking tickets and ID's.\n\nWhile we encourage you to take pictures and videos of the space, filming the comics is strictly prohibited. This show is being filmed for digital distribution. \n\nIf you have any questions, please reach out to us via Instagram @comedy.uo or email us at admin@comedyuo.com\n\n\"By entering the premises, you consent to being photographed, filmed, and/or otherwise recorded. Your entry also constitutes your consent to the use of such recordings in any and all media, now known or hereafter devised, in perpetuity, by Underground Overground LLC and/or its assignees.\n\nYou understand that all photography and recording at this event is conducted in reliance upon your entry into the premises and your consent as stated above.\"",
    img_url:
      "https://storage.googleapis.com/cuo-show-assets/ea23e42e-08c7-4423-aae3-ba47ee05caec.png",
    start: "2026-10-13T20:00:00",
    doors_open: "2026-10-13 19:00",
    background_color: "#FFAF47",
    ticket_url:
      "https://comedyuo.com/products/comedy-court-bringing-killers-to-the-courtroom",
    ticket_price: 55,
    inventory_quantity: 0,
    max_quantity_per_order: 10,
    shows: [],
    stripe_product_id: "prod_VG7xyPngjVsTVN",
    status: "active",
    published: true,
    location_name: "Fordham Law School",
    location: { id: "430", name: "Fordham Law School" },
  },
  {
    id: "9bbdc84f-0696-42d3-9dba-930315c42477",
    slug: "3rd-annual-peter-luger-comedy-show",
    title: "3rd Annual Peter Luger Comedy Show",
    variant_name: null,
    description:
      "Tickets go live October 1st at 7PM! \n\nFor the 3rd time we're about to do it real nice. Come join us Wednesday, October 14th for the 3rd annual comedy show at the legendary Peter Luger Steak House. \n\nThe show will be upstairs where the drinks will be flowing. When the show ends, head downstairs to a private dining room after closing and enjoy some Luger's burgers, fries and Schlag!\n\nShow Timing\nDoors open: 8:15pm\nShow Start: 8:45pm\n\nPeter Luger\n178 Broadway, BK\n\n-----\n\nThis is a 21+ event - security will be checking tickets and ID's.\n\nWhile we encourage you to take pictures and videos of the space, filming the comics is strictly prohibited.\n\nIf you have any questions, please reach out to us via Instagram @comedy.uo or email us at admin@comedyuo.com\n\n\"By entering the premises, you consent to being photographed, filmed, and/or otherwise recorded. Your entry also constitutes your consent to the use of such recordings in any and all media, now known or hereafter devised, in perpetuity, by Underground Overground LLC and/or its assignees.\n\nYou understand that all photography and recording at this event is conducted in reliance upon your entry into the premises and your consent as stated above.\"",
    img_url:
      "https://storage.googleapis.com/cuo-show-assets/e2cc37cd-679a-444e-a228-a3fd49390608.png",
    start: "2026-10-14T20:45:00",
    doors_open: "2026-10-14 20:15:00",
    background_color: "#FFAF47",
    ticket_url:
      "https://comedyuo.com/products/3rd-annual-peter-luger-comedy-show",
    ticket_price: 175,
    inventory_quantity: 0,
    max_quantity_per_order: 10,
    shows: [],
    stripe_product_id: "prod_VG86Ln5rSmn8R5",
    status: "active",
    published: true,
    location_name: "Peter Luger",
    location: { id: "34", name: "Peter Luger" },
  },
  {
    id: "bd4da186-8746-48e8-80ed-2c972a827aad",
    slug: "3rd-annual-lox-of-laughs-comedy-show",
    title: "3rd Annual Lox of Laughs Comedy Show",
    variant_name: null,
    description:
      "Friday, October 23rd\n\nSo excited to be back at Acme Smoked Fish to bring you another incredible comedy show at their factory in Brooklyn🐟\n\nTickets include unlimited free drinks and bagels & lox from Acme Smoked Fish & Utopia Bagels🥯\n\nShow Timing:\nDoors open: 7:30pm\nShow starts: 8:15pm\n\nAcme Smoked Fish\n30 Gem St. Brooklyn \n\n-----\n\nThis is a 21+ event - security will be checking tickets and ID's.\n\nWhile we encourage you to take pictures and videos of the space, filming the comics is strictly prohibited.\n\nIf you have any questions, please reach out to us via Instagram @comedy.uo or email us at admin@comedyuo.com\n\n\"By entering the premises, you consent to being photographed, filmed, and/or otherwise recorded. Your entry also constitutes your consent to the use of such recordings in any and all media, now known or hereafter devised, in perpetuity, by Underground Overground LLC and/or its assignees.\n\nYou understand that all photography and recording at this event is conducted in reliance upon your entry into the premises and your consent as stated above.\"",
    img_url:
      "https://storage.googleapis.com/cuo-show-assets/271286f4-1e4d-4f54-9946-44204a44a7c8.png",
    start: "2026-10-23T19:30:00",
    doors_open: "2026-10-23 19:00:00",
    background_color: "#FFAF47",
    ticket_url:
      "https://comedyuo.com/products/3rd-annual-lox-of-laughs-comedy-show",
    ticket_price: 60,
    inventory_quantity: 52,
    max_quantity_per_order: 10,
    shows: [],
    stripe_product_id: "prod_VLX2Fy8KmSsO6l",
    status: "active",
    published: true,
    location_name: "ACME Smoked Fish",
    location: { id: "33", name: "ACME Smoked Fish" },
  },
  {
    id: "9612ed25-c4be-4a0e-9062-82a694c4e6a3",
    slug: "private-loft-magic-show",
    title: "Astor Place Hairstylists Magic Show",
    variant_name: null,
    description:
      "Friday, October 30th\n \nBest costume wins! We're not sure what the prize is yet but it will be good. \n\nSo excited to be back at Astor Place Hairstylists an Iconic NYC barbershop!\n\nTickets include free drinks + afterparty👻\n\nShow Timing\nDoors open: 7:45pm\nShow Start: 8:15pm\n\nAstor Place Hairstylists\n2 Astor Place\n\n-----\n\nAn important note on timing: doors open time for shows is 30 minutes prior to showtime. Come early to enjoy some pre-show drinks.\n\nWhile we encourage you to take pictures and videos of the space, filming the magicians is strictly prohibited.\n\nThis is a 21+ event - security will be checking tickets and ID's.\n\nIf you have any questions, please reach out to us via Instagram @magic.uo or email us at admin@comedyuo.com",
    img_url:
      "https://storage.googleapis.com/cuo-show-assets/a5282332-d28f-492d-bf7b-4bea1c711edf.png",
    start: "2026-10-30T20:15:00",
    doors_open: "2026-10-30 19:45:00",
    background_color: "#FFAF47",
    ticket_url:
      "https://comedyuo.com/products/private-loft-magic-show",
    ticket_price: 65,
    inventory_quantity: 62,
    max_quantity_per_order: 10,
    shows: [],
    stripe_product_id: "prod_VMAbSQWFPOeZrp",
    status: "active",
    published: true,
    location_name: "Astor Place Hairstylists",
    location: { id: "1", name: "Astor Place Hairstylists" },
  },
];

/** Hyperspecific: KidSuper Funny Business checkout is on Funny Businesses, not Stripe. */
function withKidSuperFunnyBusiness(shows: PublicShow[]): PublicShow[] {
  return shows.map((show) => {
    if (!isKidSuperFunnyBusinessShow(show)) return show;
    return {
      ...show,
      ticket_url: KIDSUPER_FUNNY_BUSINESS_TICKET_URL,
    };
  });
}

function isPublishedShow(item: PublicShow): boolean {
  const published = item?.published;
  return published === true || published === "true" || published === 1;
}

const reducedMotionSx = {
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none !important",
    transform: "none !important",
  },
} as const;

function earliestChildStart(show: PublicShow): string | undefined {
  const children = Array.isArray(show.shows) ? show.shows : [];
  const starts = children
    .map((child: any) => (typeof child?.start === "string" ? child.start : ""))
    .filter((value: string) => value.length > 0);
  if (starts.length === 0) return undefined;

  let earliest = starts[0];
  for (const start of starts.slice(1)) {
    if (new Date(start).getTime() < new Date(earliest).getTime()) {
      earliest = start;
    }
  }
  return earliest;
}

function activeChildren(show: PublicShow): any[] {
  const children = Array.isArray(show.shows) ? show.shows : [];
  return children.filter(
    (child: any) => child?.status === "active" || child?.status == null
  );
}

function formatShowTimes(show: PublicShow, resolvedStart?: string): string {
  const children = activeChildren(show).filter(
    (child: any) => typeof child?.start === "string" && child.start.length > 0
  );

  if (children.length > 1) {
    const labels = children
      .slice()
      .sort(
        (a: any, b: any) =>
          new Date(a.start).getTime() - new Date(b.start).getTime()
      )
      .map((child: any) => {
        const d = parseShowDateTime(child.start);
        if (!d?.isValid()) return "";
        try {
          return d.format("h:mm A");
        } catch {
          return "";
        }
      })
      .filter(Boolean);
    const unique = [...new Set(labels)];
    if (unique.length > 0) return unique.join(" & ");
  }

  return formatShowStartTimeLine(resolvedStart ?? show.start);
}

function resolveTicketPrice(show: PublicShow): number | null {
  const children = activeChildren(show);
  const childPrices = children
    .map((child: any) => child?.ticket_price)
    .filter((price: unknown): price is number => typeof price === "number");
  if (childPrices.length > 0) return Math.min(...childPrices);
  return typeof show.ticket_price === "number" ? show.ticket_price : null;
}

function formatTicketPrice(price: number): string {
  if (Number.isInteger(price)) return `$${price}`;
  return `$${price.toFixed(2)}`;
}

function truncateText(text: string, max = 160): string {
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, max - 1).trimEnd()}…`;
}

function ShowCardSkeleton() {
  return (
    <Box
      sx={{
        borderRadius: "14px",
        overflow: "hidden",
        backgroundColor: CARD_BG,
        border: `1px solid ${CARD_BORDER}`,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      <Skeleton
        variant="rectangular"
        animation="wave"
        sx={{
          width: "100%",
          aspectRatio: "16 / 10",
          borderRadius: 0,
          ...skeletonLineSx,
        }}
      />
      <Box
        sx={{
          p: { xs: 2, sm: 2.25, md: 2.5 },
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Skeleton
          animation="wave"
          width="78%"
          height={28}
          sx={{ borderRadius: 0.5, ...skeletonLineSx }}
        />
        <Skeleton
          animation="wave"
          width="52%"
          height={16}
          sx={{ borderRadius: 0.5, ...skeletonLineSx }}
        />
        <Skeleton
          animation="wave"
          width="68%"
          height={16}
          sx={{ borderRadius: 0.5, ...skeletonLineSx }}
        />
        <Skeleton
          animation="wave"
          width="90%"
          height={48}
          sx={{ borderRadius: 0.5, ...skeletonLineSx }}
        />
        <Box sx={{ flexGrow: 1, minHeight: 8 }} />
        <Skeleton
          animation="wave"
          variant="rectangular"
          width="100%"
          sx={{
            height: 46,
            borderRadius: "10px",
            ...skeletonLineSx,
          }}
        />
      </Box>
    </Box>
  );
}

function showTicketHref(show: PublicShow): string {
  const children = activeChildren(show);

  if (children.length === 1) {
    const child = children[0];
    return publicShowPurchaseUrl({
      slug: child.slug ?? show.slug,
      id: child.id ?? show.id,
      title: show.title,
      variant_name: child.variant_name ?? show.variant_name,
      stripe_product_id: child.stripe_product_id ?? show.stripe_product_id,
      ticket_url: child.ticket_url ?? show.ticket_url,
    });
  }

  return publicShowPurchaseUrl(show);
}

function isExternalPurchaseUrl(href: string): boolean {
  try {
    return new URL(href, window.location.origin).origin !== window.location.origin;
  } catch {
    return false;
  }
}

function showCardAvailability(show: PublicShow): Availability {
  const children = activeChildren(show);
  if (children.length > 0) return combinedAvailability(children);
  return showAvailability(show);
}

/** Same window as GET /show/public/?future=true (yesterday 00:00 local). */
function isUpcomingShow(show: PublicShow): boolean {
  const raw = earliestChildStart(show) ?? show.start;
  const start = parseShowDateTime(raw);
  if (!start?.isValid()) return true;
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - 1);
  return start.toDate().getTime() >= cutoff.getTime();
}

function ticketsListShows(shows: PublicShow[]): PublicShow[] {
  return withKidSuperFunnyBusiness(
    shows.filter(isPublishedShow).filter(isUpcomingShow)
  );
}

function MetaRow({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          color: MUTED,
          display: "inline-flex",
          mt: "2px",
          flexShrink: 0,
          "& .MuiSvgIcon-root": { fontSize: 16 },
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontFamily: pageFont,
          fontSize: { xs: "0.875rem", sm: "0.9rem" },
          lineHeight: 1.45,
          color: "rgba(255,255,255,0.88)",
          wordBreak: "break-word",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export default function Tickets() {
  // Offline snapshot until live API responds. Includes sold-out upcoming shows.
  const [shows, setShows] = useState<PublicShow[]>(() =>
    ticketsListShows(PREVIEW_SHOWS)
  );
  const [showsLoading, setShowsLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);

    const load = async () => {
      setShowsLoading(true);
      try {
        const data = await PublicShowService.getShows({
          future: true,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const fromApi = ticketsListShows(Array.isArray(data) ? data : []);
        if (fromApi.length > 0) {
          setShows(fromApi);
        } else {
          console.warn(
            "[tickets] No published upcoming shows from API, keeping preview cards."
          );
          setShows(ticketsListShows(PREVIEW_SHOWS));
        }
      } catch (error) {
        if (controller.signal.aborted) {
          console.warn(
            "[tickets] Show fetch timed out or aborted, keeping preview cards."
          );
        } else {
          console.error(
            "[tickets] Failed to load shows, keeping preview cards.",
            error
          );
        }
        setShows(ticketsListShows(PREVIEW_SHOWS));
      } finally {
        window.clearTimeout(timeoutId);
        setShowsLoading(false);
      }
    };
    load();
    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  const fallbackTitle = "Comedy in Paradise";
  const hasUpcomingShows = showsLoading || shows.length > 0;

  const ticketsJsonLd =
    shows.length === 0
      ? undefined
      : jsonLdGraph([
          organizationSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tickets", path: "/tickets" },
          ]),
          {
            "@type": "CollectionPage",
            name: "Tickets and Upcoming Shows",
            url: NAVIGATION_URLS.TICKETS,
            about: { "@id": "https://comedyuo.com/#organization" },
          },
          {
            "@type": "ItemList",
            name: "Upcoming Comedy Shows",
            url: NAVIGATION_URLS.TICKETS,
            numberOfItems: shows.length,
            itemListElement: shows.map((show, index) => {
              const href = showTicketHref(show);
              const url =
                href && /^https?:\/\//i.test(href)
                  ? href
                  : absolutePublicUrl(href || "/tickets");
              return {
                "@type": "ListItem",
                position: index + 1,
                item: eventSchema({
                  name: show.variant_name || show.title || fallbackTitle,
                  description: show.description,
                  url,
                  image: show.img_url,
                  startDate: earliestChildStart(show) ?? show.start,
                  locationName: show.location?.name || show.location_name,
                  price: resolveTicketPrice(show),
                }),
              };
            }),
          },
        ]);

  return (
    <>
      <TicketsLayoutHelmet />
      {ticketsJsonLd ? <JsonLdScript data={ticketsJsonLd} /> : null}
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          bgcolor: PAGE_BG,
        }}
      >
        <Header variant="dark" />
        <Box
          component="main"
          sx={{
            flex: 1,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            overflowX: "hidden",
            WebkitTextSizeAdjust: "100%",
            pb: {
              xs: "max(48px, env(safe-area-inset-bottom, 0px))",
              sm: 8,
              md: 10,
            },
          }}
        >
          {/* Katz's hero band — photo behind the floating frosted panel */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              boxSizing: "border-box",
              pt: { xs: 3, sm: 5, md: 7 },
              pb: { xs: 3.25, sm: 5, md: 6 },
              backgroundImage:
                'url("https://storage.googleapis.com/cuo-website/Best+Comedy+Shows+.jpg")',
              backgroundSize: "cover",
              backgroundPosition: { xs: "center 32%", md: "center" },
              backgroundRepeat: "no-repeat",
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                background: {
                  xs: "linear-gradient(180deg, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.66) 50%, rgba(10,10,10,0.95) 100%)",
                  md: "linear-gradient(90deg, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.62) 100%), linear-gradient(180deg, transparent 70%, rgba(10,10,10,0.95) 100%)",
                },
                zIndex: 0,
              }}
            />
            <Container
              maxWidth="lg"
              sx={{
                position: "relative",
                zIndex: 1,
                width: "100%",
                maxWidth: "100%",
                pl: {
                  xs: "max(16px, env(safe-area-inset-left, 0px))",
                  sm: 3,
                  md: 4,
                },
                pr: {
                  xs: "max(16px, env(safe-area-inset-right, 0px))",
                  sm: 3,
                  md: 4,
                },
                boxSizing: "border-box",
              }}
            >
              {/* Intro — Join 'The Underground' */}
              <Box
                sx={{
                  position: "relative",
                  backgroundColor: "rgba(28, 28, 32, 0.58)",
                  backdropFilter: {
                    xs: "blur(16px) saturate(1.1)",
                    md: "blur(20px) saturate(1.12)",
                  },
                  WebkitBackdropFilter: {
                    xs: "blur(16px) saturate(1.1)",
                    md: "blur(20px) saturate(1.12)",
                  },
                  border: "1px solid rgba(255,255,255,0.14)",
                  borderRadius: { xs: "14px", md: "12px" },
                  boxShadow:
                    "inset 0 1px 0 rgba(255,255,255,0.08), 0 12px 40px rgba(0,0,0,0.4)",
                  isolation: "isolate",
                  boxSizing: "border-box",
                  overflow: "hidden",
                  px: { xs: 2, sm: 3.5, md: 4.5 },
                  py: { xs: 2.75, sm: 3.75, md: 4.5 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "stretch", md: "flex-end" },
                    justifyContent: "space-between",
                    gap: { xs: 2.5, md: 4 },
                  }}
                >
              <Box sx={{ flex: "1 1 auto", minWidth: 0, maxWidth: { md: 640 } }}>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.6,
                    mb: { xs: 1.5, sm: 2 },
                    borderRadius: "999px",
                    border: `1px solid ${CARD_BORDER}`,
                    backgroundColor: "rgba(255,255,255,0.03)",
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "#fff",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: pageFont,
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.72)",
                      lineHeight: 1,
                    }}
                  >
                    Priority Access
                  </Typography>
                </Box>

                <Typography
                  component="h1"
                  sx={{
                    fontFamily: pageFont,
                    fontWeight: 700,
                    fontSize: {
                      xs: "clamp(1.85rem, 7.2vw, 2.4rem)",
                      sm: "2.75rem",
                      md: "3.25rem",
                    },
                    lineHeight: { xs: 1.1, sm: 1.05 },
                    letterSpacing: "-0.02em",
                    color: "#fff",
                    mb: { xs: 1.25, sm: 1.5 },
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  Join &apos;The Underground&apos;
                </Typography>

                <Typography
                  sx={{
                    fontFamily: pageFont,
                    fontSize: { xs: "0.9375rem", sm: "1.05rem" },
                    lineHeight: { xs: 1.5, sm: 1.55 },
                    color: MUTED,
                    maxWidth: 520,
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  Join our exclusive list of guests that get first access to our
                  biggest shows before tickets are released to the general
                  public.
                </Typography>
              </Box>

              <Box
                sx={{
                  flex: "0 0 auto",
                  width: { xs: "100%", md: "auto" },
                  display: "flex",
                  justifyContent: { xs: "stretch", md: "flex-end" },
                  pb: { md: 0.5 },
                }}
              >
                <Button
                  component={RouterLink}
                  to="/waitlist"
                  disableElevation
                  variant="contained"
                  onClick={() => trackWaitlistClick()}
                  aria-label="Join 'The Underground' waitlist"
                  sx={{
                    width: { xs: "100%", md: "auto" },
                    minWidth: { md: 148 },
                    backgroundColor: "#f2f2f2",
                    color: PAGE_BG,
                    fontFamily: pageFont,
                    fontWeight: 700,
                    fontSize: { xs: "1rem", sm: "1rem" },
                    letterSpacing: "0.01em",
                    textTransform: "none",
                    px: { xs: 3, sm: 3.5 },
                    py: { xs: 1.5, sm: 1.45 },
                    minHeight: { xs: 52, sm: 48 },
                    borderRadius: "12px",
                    boxShadow: "none",
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                    ...reducedMotionSx,
                    transition: "background-color 0.2s ease, color 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#ffffff",
                      boxShadow: "none",
                    },
                  }}
                >
                  Join →
                </Button>
              </Box>
                </Box>
              </Box>
            </Container>
          </Box>

          <Box sx={{ width: "100%", pt: { xs: 2.5, sm: 4 }, pb: { xs: 0.5, sm: 1 } }}>
            <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
              <Box
                component={RouterLink}
                to={MERCH_HUB_HREF}
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    MERCH_THUMBS.length > 1
                      ? { xs: "1fr", md: "minmax(0, 340px) 1fr auto" }
                      : { xs: "92px 1fr", sm: "112px 1fr auto" },
                  alignItems: "center",
                  gap: { xs: 1.75, sm: 2.5 },
                  bgcolor: CARD_BG,
                  border: `1px solid ${CARD_BORDER}`,
                  borderRadius: "16px",
                  p: { xs: 1.5, sm: 2 },
                  textDecoration: "none",
                  color: "inherit",
                  transition: "border-color 0.2s ease, background-color 0.2s ease",
                  "@media (hover: hover)": {
                    "&:hover": {
                      borderColor: "rgba(255,255,255,0.28)",
                      backgroundColor: "#181818",
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${MERCH_THUMBS.length}, minmax(0, 1fr))`,
                    gap: 1,
                    width: MERCH_THUMBS.length > 1 ? "100%" : { xs: 92, sm: 112 },
                    height: MERCH_THUMBS.length > 1 ? "auto" : { xs: 72, sm: 92 },
                  }}
                >
                  {MERCH_THUMBS.map((thumb) => (
                    <Box
                      key={thumb.alt}
                      sx={{
                        aspectRatio: MERCH_THUMBS.length > 1 ? "1" : undefined,
                        height: "100%",
                        borderRadius: "10px",
                        overflow: "hidden",
                        bgcolor: "#0d0d0d",
                        border: `1px solid ${CARD_BORDER}`,
                      }}
                    >
                      <Box
                        component="img"
                        src={thumb.src}
                        alt={thumb.alt}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: MERCH_THUMBS.length > 1 ? "contain" : "cover",
                          display: "block",
                        }}
                      />
                    </Box>
                  ))}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: pageFont,
                      fontSize: "0.625rem",
                      fontWeight: 600,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: MUTED_SOFT,
                      mb: 0.75,
                    }}
                  >
                    {MERCH_HUB_EYEBROW}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: pageFont,
                      fontWeight: 700,
                      fontSize: { xs: "1.05rem", sm: "1.2rem" },
                      color: "#fff",
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  >
                    {MERCH_HUB_TITLE}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: pageFont,
                      fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                      color: MUTED,
                    }}
                  >
                    {MERCH_HUB_BLURB}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    gridColumn:
                      MERCH_THUMBS.length > 1 ? undefined : { xs: "1 / -1", sm: "auto" },
                    justifySelf: { xs: "stretch", md: "end" },
                    py: { xs: 1.25, sm: 1.15 },
                    px: { xs: 2, sm: 2.25 },
                    borderRadius: "10px",
                    border: "1px solid rgba(255,255,255,0.28)",
                    textAlign: "center",
                    fontFamily: pageFont,
                    fontWeight: 700,
                    fontSize: "0.9375rem",
                    color: "#fff",
                    whiteSpace: "nowrap",
                  }}
                >
                  Shop merch →
                </Box>
              </Box>
            </Container>
          </Box>

          <Container
            maxWidth="lg"
            sx={{
              width: "100%",
              maxWidth: "100%",
              pl: {
                xs: "max(16px, env(safe-area-inset-left, 0px))",
                sm: 3,
                md: 4,
              },
              pr: {
                xs: "max(16px, env(safe-area-inset-right, 0px))",
                sm: 3,
                md: 4,
              },
              boxSizing: "border-box",
              pt: { xs: 2.5, sm: 3.5, md: 4 },
            }}
          >
            {/* Shows grid */}
            {hasUpcomingShows ? (
              <Box
                component="section"
                aria-label="Upcoming shows"
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: { xs: 2.25, sm: 2.5, md: 3 },
                  width: "100%",
                  maxWidth: "100%",
                }}
              >
                {showsLoading && shows.length === 0
                  ? SKELETON_CARD_KEYS.map((key) => (
                      <ShowCardSkeleton key={key} />
                    ))
                  : shows.map((show) => {
                      const title =
                        show.variant_name || show.title || fallbackTitle;
                      const image = show.img_url || "/images/ticket-2.webp";
                      const resolvedStart =
                        earliestChildStart(show) ?? show.start;
                      const dateStr =
                        formatShowDateLine(resolvedStart) || "Date TBA";
                      const timeStr = formatShowTimes(show, resolvedStart);
                      const locationName =
                        show.location?.name ||
                        show.location_name ||
                        "";
                      const price = resolveTicketPrice(show);
                      const purchaseHref = showTicketHref(show);
                      const externalPurchase =
                        !!purchaseHref &&
                        isExternalPurchaseUrl(purchaseHref);
                      const availability = showCardAvailability(show);
                      const held = availability === "held";
                      const soldOut = availability !== "available";

                      const parsed = show.description
                        ? parseShowInfo(show.description, {
                            start: resolvedStart,
                            doorsOpen: show.doors_open,
                            locationName: locationName || null,
                            slug: show.slug,
                          })
                        : null;
                      const descriptionText = parsed?.introParagraphs?.[0]
                        ? truncateText(parsed.introParagraphs[0])
                        : "";
                      const perkText = parsed?.perksCallout?.trim() || "";
                      const locationDetail =
                        locationName ||
                        parsed?.locationLines?.[0] ||
                        title;

                      const cardLabel = held
                        ? `${title}: ${HELD_LABEL}`
                        : soldOut
                          ? `${title}: Sold out`
                          : `Buy tickets for ${title}`;

                      return (
                        <Box
                          key={show.id}
                          component={purchaseHref ? "a" : "article"}
                          href={purchaseHref || undefined}
                          target={externalPurchase ? "_blank" : undefined}
                          rel={
                            externalPurchase
                              ? "noopener noreferrer"
                              : undefined
                          }
                          aria-label={cardLabel}
                          sx={{
                            cursor: purchaseHref ? "pointer" : "default",
                            textDecoration: "none",
                            color: "inherit",
                            borderRadius: "14px",
                            overflow: "hidden",
                            backgroundColor: CARD_BG,
                            border: `1px solid ${CARD_BORDER}`,
                            display: "flex",
                            flexDirection: "column",
                            minWidth: 0,
                            maxWidth: "100%",
                            boxSizing: "border-box",
                            outline: "none",
                            ...reducedMotionSx,
                            transition:
                              "border-color 0.2s ease, transform 0.2s ease",
                            "&:focus-visible": {
                              boxShadow: "0 0 0 2px #fff, 0 0 0 4px #0a0a0a",
                            },
                            "@media (hover: hover)": {
                              "&:hover": {
                                borderColor: "rgba(255,255,255,0.22)",
                              },
                              "&:hover img": {
                                transform: "scale(1.03)",
                              },
                            },
                            "@media (prefers-reduced-motion: reduce)": {
                              "&:hover img": { transform: "none" },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              overflow: "hidden",
                              width: "100%",
                              aspectRatio: "16 / 10",
                              bgcolor: "#0d0d0d",
                            }}
                          >
                            <Box
                              component="img"
                              src={image}
                              alt={title}
                              sx={{
                                display: "block",
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center",
                                transition: "transform 0.35s ease",
                                ...reducedMotionSx,
                                opacity: soldOut ? 0.55 : 1,
                              }}
                            />

                            {soldOut && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 12,
                                  right: 12,
                                  px: 1.25,
                                  py: 0.55,
                                  borderRadius: "999px",
                                  fontFamily: pageFont,
                                  fontWeight: 700,
                                  fontSize: "0.6875rem",
                                  letterSpacing: "0.08em",
                                  textTransform: "uppercase",
                                  color: held ? "#fcd34d" : "#fff",
                                  backgroundColor: "rgba(0,0,0,0.78)",
                                  border: held
                                    ? "1px solid rgba(252,211,77,0.55)"
                                    : "1px solid rgba(255,255,255,0.28)",
                                }}
                              >
                                {held ? HELD_LABEL : "Sold Out"}
                              </Box>
                            )}

                            <Box
                              sx={{
                                position: "absolute",
                                left: 12,
                                bottom: 12,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                px: 1.15,
                                py: 0.55,
                                borderRadius: "999px",
                                backgroundColor: "rgba(0,0,0,0.72)",
                                border: "1px solid rgba(255,255,255,0.18)",
                              }}
                            >
                              <ConfirmationNumberOutlinedIcon
                                sx={{
                                  fontSize: 14,
                                  color: "rgba(255,255,255,0.75)",
                                }}
                              />
                              <Typography
                                component="span"
                                sx={{
                                  fontFamily: pageFont,
                                  fontSize: "0.625rem",
                                  fontWeight: 600,
                                  letterSpacing: "0.1em",
                                  textTransform: "uppercase",
                                  color: "rgba(255,255,255,0.78)",
                                  lineHeight: 1,
                                }}
                              >
                                Admit One
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              p: { xs: 2, sm: 2.25, md: 2.5 },
                              display: "flex",
                              flexDirection: "column",
                              flex: 1,
                              minWidth: 0,
                              gap: { xs: 1.5, sm: 1.75 },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: {
                                  xs: "stretch",
                                  sm: "flex-start",
                                },
                                justifyContent: "space-between",
                                gap: { xs: 1, sm: 2 },
                                minWidth: 0,
                              }}
                            >
                              <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                                <Typography
                                  component="h2"
                                  sx={{
                                    fontFamily: pageFont,
                                    fontWeight: 700,
                                    fontSize: {
                                      xs: "1.0625rem",
                                      sm: "1.125rem",
                                      md: "1.2rem",
                                    },
                                    lineHeight: 1.3,
                                    color: "#fff",
                                    wordBreak: "break-word",
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  {title}
                                </Typography>
                              </Box>

                              {price != null && (
                                <Box
                                  sx={{
                                    flex: "0 0 auto",
                                    display: "flex",
                                    flexDirection: {
                                      xs: "row",
                                      sm: "column",
                                    },
                                    alignItems: {
                                      xs: "baseline",
                                      sm: "flex-end",
                                    },
                                    gap: { xs: 0.75, sm: 0 },
                                    textAlign: { xs: "left", sm: "right" },
                                    pl: { sm: 1 },
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: pageFont,
                                      fontWeight: 700,
                                      fontSize: {
                                        xs: "1.2rem",
                                        sm: "1.25rem",
                                      },
                                      lineHeight: 1.1,
                                      color: "#fff",
                                    }}
                                  >
                                    {formatTicketPrice(price)}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontFamily: pageFont,
                                      fontSize: "0.625rem",
                                      fontWeight: 600,
                                      letterSpacing: "0.1em",
                                      textTransform: "uppercase",
                                      color: MUTED_SOFT,
                                      mt: { sm: 0.35 },
                                    }}
                                  >
                                    Per Ticket
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.9,
                              }}
                            >
                              <MetaRow icon={<CalendarTodayOutlinedIcon />}>
                                {dateStr}
                              </MetaRow>
                              {timeStr ? (
                                <MetaRow icon={<AccessTimeOutlinedIcon />}>
                                  {timeStr}
                                </MetaRow>
                              ) : null}
                              <MetaRow icon={<LocationOnOutlinedIcon />}>
                                {locationDetail}
                              </MetaRow>
                            </Box>

                            {(descriptionText || perkText) && (
                              <Box
                                sx={{
                                  borderTop: `1px solid ${CARD_BORDER}`,
                                  pt: 1.75,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1.25,
                                }}
                              >
                                {descriptionText ? (
                                  <Typography
                                    sx={{
                                      fontFamily: pageFont,
                                      fontSize: "0.875rem",
                                      lineHeight: 1.55,
                                      color: MUTED,
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {descriptionText}
                                  </Typography>
                                ) : null}

                                {perkText ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: 0.85,
                                    }}
                                  >
                                    <CheckIcon
                                      sx={{
                                        fontSize: 16,
                                        color: "rgba(255,255,255,0.85)",
                                        mt: "2px",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontFamily: pageFont,
                                        fontSize: "0.8125rem",
                                        lineHeight: 1.45,
                                        color: MUTED,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {perkText}
                                    </Typography>
                                  </Box>
                                ) : null}
                              </Box>
                            )}

                            <Box sx={{ flexGrow: 1, minHeight: 4 }} />

                            <Box
                              sx={{
                                mt: 0.5,
                                py: { xs: 1.4, sm: 1.25 },
                                px: 2,
                                borderRadius: "10px",
                                textAlign: "center",
                                fontFamily: pageFont,
                                fontWeight: 700,
                                fontSize: { xs: "0.9375rem", sm: "0.9375rem" },
                                letterSpacing: "0.02em",
                                color: soldOut
                                  ? "rgba(255,255,255,0.55)"
                                  : "#fff",
                                backgroundColor: "transparent",
                                border: soldOut
                                  ? "1px solid rgba(255,255,255,0.18)"
                                  : "1px solid rgba(255,255,255,0.35)",
                                ...reducedMotionSx,
                                transition:
                                  "background-color 0.2s ease, border-color 0.2s ease",
                                flexShrink: 0,
                                minHeight: { xs: 48, sm: 46 },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxSizing: "border-box",
                                WebkitTapHighlightColor: "transparent",
                                "@media (hover: hover)": soldOut
                                  ? undefined
                                  : {
                                      "a:hover &": {
                                        backgroundColor:
                                          "rgba(255,255,255,0.08)",
                                        borderColor: "rgba(255,255,255,0.55)",
                                      },
                                    },
                              }}
                            >
                              {held ? HELD_LABEL : soldOut ? "Sold Out" : "Buy Ticket"}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
              </Box>
            ) : null}
          </Container>
        </Box>

        <Box sx={{ flexShrink: 0, width: "100%", maxWidth: "100%" }}>
          <Footer />
        </Box>
      </Box>
    </>
  );
}
